/**
 * Liga a ligação única de tempo real do separador à cache do TanStack Query (ADR-0017).
 *
 * Cada evento entra na cache com `setQueryData` — nunca um `invalidateQueries` por mensagem: as mensagens novas aparecem
 * sem pedidos, sem refetch por temporizador e sem recarregar. Só mudanças de estrutura (renomear, entradas e saídas)
 * voltam a ler o detalhe da conversa. Se o WebSocket falhar por completo, consulta-se por cursor até voltar.
 *
 * A ligação é partilhada por contagem de referências: montar o hook em dois sítios não abre duas.
 */

import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import { lastSeq, markDeleted, upsertMessage, emptyWindow, type MessageWindow } from "@/lib/chat/message-window";
import { RealtimeConnection, type ConnectionStatus } from "@/lib/chat/realtime-connection";
import type { ChatRealtimeEvent } from "@/lib/chat/realtime-events";
import { applyNewMessage, applyRead, removeConversation, type ChatOverview } from "@/lib/chat/sidebar";
import { applyBotEvent, botKeys } from "./use-chat-bot";
import { setStorageOwner } from "@/lib/chat/local-store";
import type { LiveBotRuns } from "@/lib/chat/bot-runs";
import { CHAT_ROOT, chatKeys } from "./people-chat-keys";
import { bellKey } from "./use-chat-notifications";
import { replayOutbox } from "./use-people-chat";
import { receiveNotification } from "./use-chat-notifications";

const FALLBACK_POLL_MS = 5000;
const TYPING_TTL_MS = 6000;

// ── estado efémero partilhado pelo separador (presença, «a escrever», ligação) ────────────────────
type Listener = () => void;
const listeners = new Set<Listener>();
let status: ConnectionStatus = "a_ligar";
const online = new Set<string>();
const typing = new Map<string, Map<string, number>>(); // conversa → pessoa → expira
let viewing: string | null = null;
let me: string | null = null;
let version = 0;
// O instante do último aviso aos componentes: o «a escrever» expira por ele, sem ler o relógio durante o render.
let emittedAt = 0;

function emit(): void {
  version += 1;
  emittedAt = Date.now();
  for (const l of listeners) l();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useConnectionStatus(): ConnectionStatus {
  return useSyncExternalStore(subscribe, () => status);
}

export function useIsOnline(userId: string | null | undefined): boolean {
  useSyncExternalStore(subscribe, () => version);
  return !!userId && online.has(userId);
}

export function useTypingUsers(conversationId: string | null): string[] {
  useSyncExternalStore(subscribe, () => version);
  if (!conversationId) return [];
  return [...(typing.get(conversationId)?.entries() ?? [])].filter(([, until]) => until > emittedAt).map(([id]) => id);
}

/** A conversa que está à frente da pessoa: as mensagens novas nela contam já como lidas. */
export function setViewingConversation(conversationId: string | null): void {
  viewing = conversationId;
}

// ── a ligação ───────────────────────────────────────────────────────────────────────────────
let connection: RealtimeConnection | null = null;
let refs = 0;
let fallbackTimer: ReturnType<typeof setInterval> | null = null;
let downSince: string | null = null;
let polls = 0;

function resumeCursors(qc: QueryClient): Record<string, number> {
  const cursors: Record<string, number> = {};
  for (const [key, data] of qc.getQueriesData<MessageWindow>({ queryKey: [...CHAT_ROOT, "messages"] })) {
    const conversationId = key[3];
    if (typeof conversationId === "string" && key[4] === "main") cursors[conversationId] = lastSeq(data);
  }
  return cursors;
}

let readyBefore = false;
let typingTimer: ReturnType<typeof setTimeout> | null = null;

/** Um só temporizador para o «a escrever»: tira quem expirou e volta a avisar enquanto houver alguém. */
function scheduleTypingPrune(): void {
  if (typingTimer) return;
  typingTimer = setTimeout(() => {
    typingTimer = null;
    const now = Date.now();
    for (const [conversationId, byUser] of typing) {
      for (const [userId, until] of byUser) if (until <= now) byUser.delete(userId);
      if (byUser.size === 0) typing.delete(conversationId);
    }
    emit();
    if (typing.size > 0) scheduleTypingPrune();
  }, TYPING_TTL_MS + 50);
}

// As respostas de thread já contadas (limitado: só serve para não contar duas vezes a mesma).
const countedReplies = new Set<string>();
function counted(messageId: string): boolean {
  if (countedReplies.has(messageId)) return true;
  countedReplies.add(messageId);
  if (countedReplies.size > 2000) countedReplies.delete(countedReplies.values().next().value as string);
  return false;
}

export function applyEvent(qc: QueryClient, event: ChatRealtimeEvent): void {
  switch (event.type) {
    case "message.created":
    case "message.updated": {
      const m = event.message;
      qc.setQueryData<MessageWindow>(chatKeys.window(m.conversation_id, m.thread_root_id ?? null), (w) => (w ? upsertMessage(w, m) : w));
      if (event.type === "message.created") {
        // Uma resposta do bot pode trazer uma ação a confirmar: o cartão lê-se de novo. E a resposta chegou: a
        // execução a meio dessa janela sai (mesmo que o `bot.done` se tenha perdido numa queda da ligação).
        if (m.author_kind === "bot") {
          void qc.invalidateQueries({ queryKey: botKeys.actions(m.conversation_id) });
          qc.setQueryData<LiveBotRuns>(botKeys.runs(m.conversation_id), (runs) =>
            runs ? Object.fromEntries(Object.entries(runs).filter(([, r]) => r.threadRootId !== (m.thread_root_id ?? null))) : runs,
          );
        }
        // O contador da thread só sobe uma vez por resposta: a retoma e a consulta por cursor repetem mensagens.
        if (m.thread_root_id && !counted(m.id)) {
          // A resposta numa thread atualiza o contador da mensagem-mãe na conversa principal.
          qc.setQueryData<MessageWindow>(chatKeys.window(m.conversation_id, null), (w) =>
            w ? { ...w, items: w.items.map((x) => (x.id === m.thread_root_id ? { ...x, reply_count: x.reply_count + 1, last_reply_at: m.created_at } : x)) } : w,
          );
        }
        const visible = typeof document === "undefined" || document.visibilityState === "visible";
        qc.setQueryData<ChatOverview>(chatKeys.overview(), (o) =>
          o ? applyNewMessage(o, { conversationId: m.conversation_id, seq: m.seq, createdAt: m.created_at, mine: m.author?.id === me, viewing: visible && viewing === m.conversation_id }) : o,
        );
      }
      return;
    }
    case "message.deleted":
      for (const [key] of qc.getQueriesData<MessageWindow>({ queryKey: chatKeys.windowsOf(event.conversation_id) })) {
        qc.setQueryData<MessageWindow>(key, (w) => markDeleted(w ?? emptyWindow(), event.message_id));
      }
      return;
    case "read.updated":
      if (event.user_id === me) {
        qc.setQueryData<ChatOverview>(chatKeys.overview(), (o) => (o ? applyRead(o, event.conversation_id, event.seq) : o));
        // Ler a conversa lê no servidor os avisos dela: o sino (e o selo do menu, que partilha a cache) acompanha.
        void qc.invalidateQueries({ queryKey: bellKey });
      }
      return;
    case "conversation.deleted":
    case "conversation.removed":
      // Eliminada, ou esta pessoa deixou de a poder ler: sai da barra e as mensagens saem da memória.
      qc.setQueryData<ChatOverview>(chatKeys.overview(), (o) => (o ? removeConversation(o, event.conversation_id) : o));
      qc.removeQueries({ queryKey: chatKeys.windowsOf(event.conversation_id) });
      void qc.invalidateQueries({ queryKey: chatKeys.conversation(event.conversation_id) });
      return;
    case "conversation.updated":
    case "member.joined":
    case "member.left":
      // Estrutura, não mensagens: volta a ler-se só o que mudou.
      void qc.invalidateQueries({ queryKey: chatKeys.conversation(event.conversation_id) });
      void qc.invalidateQueries({ queryKey: chatKeys.members(event.conversation_id) });
      // Uma ação do bot decidida (ou proposta) chega como «conversa atualizada»: o cartão volta a ler-se.
      if (event.type === "conversation.updated") {
        void qc.invalidateQueries({ queryKey: botKeys.actions(event.conversation_id) });
        // O título (por exemplo o da conversa com o bot, gerado depois da 1.ª mensagem) mostra-se na barra lateral.
        void qc.invalidateQueries({ queryKey: chatKeys.overview() });
      }
      if (event.type !== "conversation.updated" && event.user_id === me) void qc.invalidateQueries({ queryKey: chatKeys.overview() });
      return;
    case "resync.required":
      if (event.conversation_id) void qc.invalidateQueries({ queryKey: chatKeys.windowsOf(event.conversation_id) });
      else void qc.invalidateQueries({ queryKey: CHAT_ROOT });
      return;
    case "typing": {
      const byUser = typing.get(event.conversation_id) ?? new Map<string, number>();
      byUser.set(event.user_id, Date.now() + TYPING_TTL_MS);
      typing.set(event.conversation_id, byUser);
      emit();
      scheduleTypingPrune();
      return;
    }
    case "presence":
      if (event.status === "offline") online.delete(event.user_id);
      else online.add(event.user_id);
      emit();
      return;
    case "bot.step":
    case "bot.delta":
    case "bot.done":
      applyBotEvent(qc, event);
      return;
    case "notification":
      receiveNotification(qc, event.notification, event.silent, viewing);
      return;
    case "ready":
      me = event.user_id;
      if (readyBefore) {
        // Voltou a ligar: a barra e o sino podem ter mudado em conversas que não estão abertas (a retoma por cursor só
        // cobre as janelas carregadas).
        void qc.invalidateQueries({ queryKey: chatKeys.overview() });
        void qc.invalidateQueries({ queryKey: bellKey });
      }
      readyBefore = true;
      void replayOutbox(qc);
      return;
    case "heartbeat":
      return;
  }
}

function startFallback(qc: QueryClient): void {
  if (fallbackTimer) return;
  downSince ??= new Date().toISOString();
  fallbackTimer = setInterval(async () => {
    const cursors = resumeCursors(qc);
    const param = Object.entries(cursors).map(([id, seq]) => `${id}:${seq}`).join(",");
    try {
      const page = await api.get("/chat/events", { query: { cursors: param, since: downSince ?? undefined } });
      downSince = new Date().toISOString();
      polls += 1;
      // A consulta por cursor só cobre as conversas carregadas: de tempos a tempos, a barra e o sino releem-se.
      if (polls % 6 === 0) {
        void qc.invalidateQueries({ queryKey: chatKeys.overview() });
        void qc.invalidateQueries({ queryKey: bellKey });
      }
      for (const event of page.events) applyEvent(qc, event);
      for (const id of page.resync_conversation_ids) void qc.invalidateQueries({ queryKey: chatKeys.windowsOf(id) });
    } catch {
      // continua a tentar: os rascunhos e as mensagens por enviar estão guardados
    }
  }, FALLBACK_POLL_MS);
}

function stopFallback(): void {
  if (fallbackTimer) clearInterval(fallbackTimer);
  fallbackTimer = null;
  downSince = null;
}

/** Monta a ligação (uma por separador). `myUserId` serve para não contar as próprias mensagens como não lidas. */
export function useChatRealtime(enabled: boolean, myUserId: string | null): RealtimeConnection | null {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    me = myUserId;
    setStorageOwner(myUserId);
    refs += 1;
    if (!connection) {
      connection = new RealtimeConnection({
        onEvent: (event) => applyEvent(qc, event),
        onStatus: (next) => {
          status = next;
          // Sem ligação, a presença que se tinha já não é de confiar: volta a chegar quando a ligação voltar.
          if (next !== "ligado") online.clear();
          emit();
        },
        resumeState: () => ({ cursors: resumeCursors(qc), since: downSince }),
        onFallback: (active) => (active ? startFallback(qc) : stopFallback()),
      });
      connection.start();
    }
    const onVisibility = () => connection?.presence(document.visibilityState === "visible" ? "online" : "ausente");
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      refs -= 1;
      if (refs === 0) {
        connection?.stop();
        connection = null;
        stopFallback();
        online.clear();
        typing.clear();
        readyBefore = false;
      }
    };
  }, [enabled, myUserId, qc]);
  return enabled ? connection : null;
}

export function getRealtimeConnection(): RealtimeConnection | null {
  return connection;
}
